import React, { useMemo, useState } from "react";
import {
  Box,
  Chip,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import DuplicateScoreBar from "./DuplicateScoreBar";
import PortfolioStatusChip from "./PortfolioStatusChip";
import {
  formatCallsPerDay,
  sortPortfolioRows,
  type PortfolioApiRow,
} from "../../utils/portfolioIntelligenceUtils";

type SortKey = "duplicateScore" | "name" | "callsPerDay" | "consumers";

interface PortfolioApiTableProps {
  rows: PortfolioApiRow[];
  loading: boolean;
}

const PortfolioApiTable: React.FC<PortfolioApiTableProps> = ({ rows, loading }) => {
  const navigate = useNavigate();
  const [sortKey, setSortKey] = useState<SortKey>("duplicateScore");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [selectedUuid, setSelectedUuid] = useState<string | null>(null);

  const sortedRows = useMemo(
    () => sortPortfolioRows(rows, sortKey, sortDir),
    [rows, sortKey, sortDir],
  );

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    setSortDir(key === "name" ? "asc" : "desc");
  };

  const handleRowClick = (row: PortfolioApiRow) => {
    setSelectedUuid(row.api.uuid);
    navigate(`/api-explorer/${row.api.uuid}`);
  };

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>
              <TableSortLabel
                active={sortKey === "name"}
                direction={sortKey === "name" ? sortDir : "asc"}
                onClick={() => handleSort("name")}
              >
                API NAME / PATH
              </TableSortLabel>
            </TableCell>
            <TableCell>DOMAIN</TableCell>
            <TableCell align="right">
              <TableSortLabel
                active={sortKey === "consumers"}
                direction={sortKey === "consumers" ? sortDir : "desc"}
                onClick={() => handleSort("consumers")}
              >
                CONSUMERS
              </TableSortLabel>
            </TableCell>
            <TableCell align="right">
              <TableSortLabel
                active={sortKey === "callsPerDay"}
                direction={sortKey === "callsPerDay" ? sortDir : "desc"}
                onClick={() => handleSort("callsPerDay")}
              >
                CALLS / DAY
              </TableSortLabel>
            </TableCell>
            <TableCell>
              <TableSortLabel
                active={sortKey === "duplicateScore"}
                direction={sortKey === "duplicateScore" ? sortDir : "desc"}
                onClick={() => handleSort("duplicateScore")}
              >
                DUPLICATE SCORE
              </TableSortLabel>
            </TableCell>
            <TableCell>STATUS</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            [1, 2, 3, 4, 5].map((i) => (
              <TableRow key={i}>
                <TableCell><Skeleton width={180} /></TableCell>
                <TableCell><Skeleton width={60} /></TableCell>
                <TableCell align="right"><Skeleton width={50} /></TableCell>
                <TableCell align="right"><Skeleton width={40} /></TableCell>
                <TableCell><Skeleton width={120} /></TableCell>
                <TableCell><Skeleton width={100} /></TableCell>
              </TableRow>
            ))
          ) : sortedRows.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6}>
                <Typography color="text.secondary" sx={{ py: 3, textAlign: "center" }}>
                  No APIs registered yet.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            sortedRows.map((row) => {
              const isSelected = selectedUuid === row.api.uuid;
              return (
                <TableRow
                  key={row.api.uuid}
                  hover
                  onClick={() => handleRowClick(row)}
                  sx={{
                    cursor: "pointer",
                    bgcolor: isSelected ? "action.selected" : undefined,
                  }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>
                      {row.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {row.path}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={row.domain}
                      size="small"
                      variant="outlined"
                      sx={{ borderColor: "divider", color: "text.secondary" }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{row.consumers} apps</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2">{formatCallsPerDay(row.callsPerDay)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Box sx={{ maxWidth: 180 }}>
                      <DuplicateScoreBar score={row.duplicateScore} status={row.status} />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <PortfolioStatusChip status={row.status} />
                  </TableCell>
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default PortfolioApiTable;
